/**
 * Navigation Evaluation Engine
 * Implements three-layer filtering architecture:
 * Layer 1: Commercial Boundary (Module Entitlements)
 * Layer 2: Security Boundary (User Permissions)
 * Layer 3: Spatial Context Boundary (Node Selection)
 * Layer 4: Advanced Conditional Display
 */

import {
  NavigationConfig,
  NavigationContext,
  NavigationEvaluationResult,
  ConditionalDisplay,
} from '../../types/navigation.types';

export class NavigationEvaluator {
  /**
   * Evaluate if a navigation item should be visible and enabled
   */
  evaluate(
    item: NavigationConfig,
    context: NavigationContext
  ): NavigationEvaluationResult {
    // Layer 1: Commercial Boundary - Check module entitlements
    const moduleCheck = this.evaluateModuleRequirement(item, context);
    if (!moduleCheck.passed) {
      return {
        visible: false,
        enabled: false,
        reason: moduleCheck.reason,
      };
    }

    // Layer 2: Security Boundary - Check user permissions
    const permissionCheck = this.evaluatePermissionRequirement(item, context);
    if (!permissionCheck.passed) {
      return {
        visible: false,
        enabled: false,
        reason: permissionCheck.reason,
      };
    }

    // Layer 3: Spatial Context Boundary - Check node binding
    const contextCheck = this.evaluateNodeBinding(item, context);
    if (!contextCheck.passed) {
      return {
        visible: false,
        enabled: false,
        reason: contextCheck.reason,
      };
    }

    // Layer 4: Advanced Conditional Display
    if (item.conditionalDisplay) {
      const conditionalCheck = this.evaluateConditionalDisplay(
        item.conditionalDisplay,
        context
      );
      if (!conditionalCheck.passed) {
        return {
          visible: false,
          enabled: false,
          reason: conditionalCheck.reason,
        };
      }
    }

    // All checks passed
    return {
      visible: true,
      enabled: true,
      badge: item.badge,
    };
  }

  /**
   * Layer 1: Evaluate module entitlement requirement
   */
  private evaluateModuleRequirement(
    item: NavigationConfig,
    context: NavigationContext
  ): { passed: boolean; reason?: string } {
    // No module requirement = always pass
    if (!item.requiredModule) {
      return { passed: true };
    }

    const requiredModules = Array.isArray(item.requiredModule)
      ? item.requiredModule
      : [item.requiredModule];

    // Check if organization has ANY of the required modules
    const hasModule = requiredModules.some((module) =>
      context.organizationModules.includes(module)
    );

    if (!hasModule) {
      return {
        passed: false,
        reason: `Module not provisioned: ${requiredModules.join(' or ')}`,
      };
    }

    return { passed: true };
  }

  /**
   * Layer 2: Evaluate permission requirement
   */
  private evaluatePermissionRequirement(
    item: NavigationConfig,
    context: NavigationContext
  ): { passed: boolean; reason?: string } {
    // No permission requirement = always pass
    if (!item.requiredPermission) {
      return { passed: true };
    }

    const requiredPermissions = Array.isArray(item.requiredPermission)
      ? item.requiredPermission
      : [item.requiredPermission];

    // Check if user has ANY of the required permissions
    const hasPermission = requiredPermissions.some((permission) => {
      // Support wildcard permissions (e.g., "*.*" for platform admin)
      if (context.userPermissions.includes('*.*')) {
        return true;
      }

      // Support category wildcards (e.g., "telemetry:*")
      const [category, action] = permission.split(':');
      const categoryWildcard = `${category}:*`;
      if (context.userPermissions.includes(categoryWildcard)) {
        return true;
      }

      // Exact permission match
      return context.userPermissions.includes(permission);
    });

    if (!hasPermission) {
      return {
        passed: false,
        reason: `Missing permission: ${requiredPermissions.join(' or ')}`,
      };
    }

    return { passed: true };
  }

  /**
   * Layer 3: Evaluate node binding filter (spatial context)
   */
  private evaluateNodeBinding(
    item: NavigationConfig,
    context: NavigationContext
  ): { passed: boolean; reason?: string } {
    // No node binding = always pass
    if (!item.nodeBindingFilter) {
      return { passed: true };
    }

    // No node selected = fail if binding required
    if (!context.selectedNodeType) {
      return {
        passed: false,
        reason: 'No spatial node selected',
      };
    }

    const requiredNodeTypes = Array.isArray(item.nodeBindingFilter)
      ? item.nodeBindingFilter
      : [item.nodeBindingFilter];

    // Check if selected node type matches ANY required type
    const matchesNodeType = requiredNodeTypes.includes(context.selectedNodeType);

    if (!matchesNodeType) {
      return {
        passed: false,
        reason: `Node type mismatch: expected ${requiredNodeTypes.join(' or ')}, got ${context.selectedNodeType}`,
      };
    }

    return { passed: true };
  }

  /**
   * Layer 4: Evaluate advanced conditional display logic
   */
  private evaluateConditionalDisplay(
    conditional: ConditionalDisplay,
    context: NavigationContext
  ): { passed: boolean; reason?: string } {
    const results = conditional.conditions.map((condition) =>
      this.evaluateCondition(condition, context)
    );

    // AND logic: all conditions must pass
    if (conditional.when === 'all') {
      const allPassed = results.every((r) => r.passed);
      if (!allPassed) {
        const failedReasons = results
          .filter((r) => !r.passed)
          .map((r) => r.reason)
          .join(', ');
        return {
          passed: false,
          reason: `Conditional display failed (ALL): ${failedReasons}`,
        };
      }
      return { passed: true };
    }

    // OR logic: at least one condition must pass
    if (conditional.when === 'any') {
      const anyPassed = results.some((r) => r.passed);
      if (!anyPassed) {
        return {
          passed: false,
          reason: 'Conditional display failed (ANY): no conditions met',
        };
      }
      return { passed: true };
    }

    return { passed: false, reason: 'Invalid conditional logic' };
  }

  /**
   * Evaluate a single condition
   */
  private evaluateCondition(
    condition: ConditionalDisplay['conditions'][0],
    context: NavigationContext
  ): { passed: boolean; reason?: string } {
    let actualValue: any;

    // Get actual value based on condition type
    switch (condition.type) {
      case 'feature_flag':
        actualValue = context.featureFlags[condition.key];
        break;
      case 'user_property':
        actualValue = context.userProperties[condition.key];
        break;
      case 'org_setting':
        actualValue = context.organizationSettings[condition.key];
        break;
      case 'custom':
        // Custom conditions can be extended here
        actualValue = undefined;
        break;
      default:
        return {
          passed: false,
          reason: `Unknown condition type: ${condition.type}`,
        };
    }

    // Evaluate operator
    const passed = this.evaluateOperator(
      actualValue,
      condition.operator,
      condition.value
    );

    if (!passed) {
      return {
        passed: false,
        reason: `Condition failed: ${condition.type}.${condition.key} ${condition.operator} ${condition.value}`,
      };
    }

    return { passed: true };
  }

  /**
   * Evaluate comparison operator
   */
  private evaluateOperator(
    actualValue: any,
    operator: string,
    expectedValue: any
  ): boolean {
    switch (operator) {
      case 'equals':
        return actualValue === expectedValue;
      case 'contains':
        if (typeof actualValue === 'string') {
          return actualValue.includes(expectedValue);
        }
        if (Array.isArray(actualValue)) {
          return actualValue.includes(expectedValue);
        }
        return false;
      case 'greater_than':
        return actualValue > expectedValue;
      case 'less_than':
        return actualValue < expectedValue;
      case 'exists':
        return actualValue !== undefined && actualValue !== null;
      default:
        return false;
    }
  }

  /**
   * Batch evaluate multiple items (optimized)
   */
  evaluateBatch(
    items: NavigationConfig[],
    context: NavigationContext
  ): Map<string, NavigationEvaluationResult> {
    const results = new Map<string, NavigationEvaluationResult>();

    for (const item of items) {
      results.set(item.id, this.evaluate(item, context));

      // Recursively evaluate children
      if (item.children) {
        const childResults = this.evaluateBatch(item.children, context);
        childResults.forEach((result, id) => results.set(id, result));
      }
    }

    return results;
  }

  /**
   * Check if user has platform admin privileges
   */
  isPlatformAdmin(context: NavigationContext): boolean {
    return (
      context.userPermissions.includes('*.*') ||
      context.userPermissions.includes('platform:admin')
    );
  }

  /**
   * Check if user has organization admin privileges
   */
  isOrganizationAdmin(context: NavigationContext): boolean {
    return (
      this.isPlatformAdmin(context) ||
      context.userPermissions.includes('organization:admin') ||
      context.userPermissions.includes('organization:*')
    );
  }

  /**
   * Get visible item count for a section
   */
  getVisibleItemCount(
    items: NavigationConfig[],
    context: NavigationContext
  ): number {
    let count = 0;

    for (const item of items) {
      const result = this.evaluate(item, context);
      if (result.visible) {
        count++;
        if (item.children) {
          count += this.getVisibleItemCount(item.children, context);
        }
      }
    }

    return count;
  }
}

// Singleton instance
export const navigationEvaluator = new NavigationEvaluator();

// Made with Bob
